// Local draft persistence utility for editor drafts (MVP)
// - Storage: localStorage with per-plot records
// - Retention: head + last_good, TTL hours
// - Size limits: soft per-plot cap; caller decides UX on failure

export interface EditorWorkingState {
  plotId: string;
  projectId?: string;
  plotlyJson: any;
}

export interface VersionSnapshot {
  plotId: string;
  versionId: string;
  plotlyJson: any;
  updatedAt?: number;
  hash: string;
}

export type DraftPatchKind = 'full' | 'partial';

export interface DraftPatch {
  kind: DraftPatchKind;
  baselineHash: string; // hash of the baseline snapshot used when creating this patch
  updatedAt: number; // ms epoch
  payload: any; // if full: entire normalized figure; if partial: { data?, layout? }
}

export interface LocalDraftRecord {
  plotId: string;
  projectId?: string;
  head?: DraftPatch;
  last_good?: DraftPatch;
  dirtySince?: number; // ms epoch when diverged from baseline
  uncleanClose?: boolean; // set when last session likely crashed
}

/** Config - can be overridden via environment (Vite) */
export const DraftConfig = {
  DRAFT_TTL_HOURS: Number((import.meta as any)?.env?.VITE_DRAFT_TTL_HOURS ?? 24),
  MAX_DRAFT_BYTES: Number((import.meta as any)?.env?.VITE_MAX_DRAFT_BYTES ?? 512 * 1024),
  MAX_TOTAL_BYTES: Number((import.meta as any)?.env?.VITE_MAX_TOTAL_DRAFTS_BYTES ?? 5 * 1024 * 1024),
} as const;

function storageKey(projectId: string | undefined, plotId: string) {
  const proj = projectId ? projectId : 'global';
  return `lumiplot.drafts.${proj}.${plotId}`;
}

function activeKeyPrefix(projectId: string | undefined, plotId: string) {
  const proj = projectId ? projectId : 'global';
  return `lumiplot.drafts.${proj}.${plotId}.active.`;
}

function legacyActiveKey(projectId: string | undefined, plotId: string) {
  const proj = projectId ? projectId : 'global';
  return `lumiplot.drafts.${proj}.${plotId}.active`;
}

function nowMs() {
  return Date.now();
}

// Stable stringify: sorts object keys recursively to ensure stable hashing
export function stableStringify(value: any): string {
  const seen = new WeakSet();
  const recur = (v: any): any => {
    if (v === null || typeof v !== 'object') return v;
    if (seen.has(v)) return undefined; // break cycles defensively
    seen.add(v);
    if (Array.isArray(v)) return v.map(recur);
    const obj: Record<string, any> = {};
    Object.keys(v).sort().forEach((k) => {
      obj[k] = recur(v[k]);
    });
    return obj;
  };
  return JSON.stringify(recur(value));
}

// Normalize figure for hashing by removing ephemeral keys (editrevision, preview-only flags)
export function normalizeFigureForHash(fig: any): any {
  try {
    const clone = structuredClone(fig ?? {});
    if (!clone.layout) clone.layout = {};
    // Remove volatile preview/editor-only fields
    if (clone.layout) {
      delete clone.layout.editrevision;
      delete clone.layout._previewScale;
      delete clone.layout._publication;
      delete clone.layout.selectionrevision;
    }
    // Remove transient trace props if present
    if (Array.isArray(clone.data)) {
      for (const t of clone.data) {
        if (!t) continue;
        delete (t as any).uid;
      }
    }
    return clone;
  } catch {
    return fig;
  }
}

// Simple FNV-1a 32-bit hash → hex string
export function hashString(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  // Convert to unsigned hex
  return (h >>> 0).toString(16).padStart(8, '0');
}

export function computeFigureHash(fig: any): string {
  return hashString(stableStringify(normalizeFigureForHash(fig)));
}

function approxSizeBytes(obj: any): number {
  try {
    return new Blob([JSON.stringify(obj)]).size;
  } catch {
    try { return JSON.stringify(obj).length; } catch { return 0; }
  }
}

/** Build a minimal patch vs baseline. MVP: if within size cap, store full normalized figure; else partial top-level keys. */
export function buildDraftPatch(baselineHash: string, baselineFig: any, workingFig: any): DraftPatch {
  // Allow sentinel EMPTY baselineHash with null baselineFigure
  const normalized = normalizeFigureForHash(workingFig);
  const fullCandidate: DraftPatch = {
    kind: 'full',
    baselineHash,
    updatedAt: nowMs(),
    payload: normalized,
  };
  if (approxSizeBytes(fullCandidate) <= DraftConfig.MAX_DRAFT_BYTES) {
    return fullCandidate;
  }
  // Partial: include only changed top-level sections
  const baseNorm = normalizeFigureForHash(baselineFig || {});
  const partial: any = {};
  if (stableStringify(baseNorm.data) !== stableStringify(normalized.data)) {
    partial.data = normalized.data;
  }
  if (stableStringify(baseNorm.layout) !== stableStringify(normalized.layout)) {
    partial.layout = normalized.layout;
  }
  const partialPatch: DraftPatch = {
    kind: 'partial',
    baselineHash,
    updatedAt: nowMs(),
    payload: partial,
  };
  return partialPatch;
}

export function applyDraftPatch(baselineFig: any, patch: DraftPatch): any {
  if (patch.kind === 'full') return structuredClone(patch.payload);
  const merged = structuredClone(baselineFig || {});
  if (patch.payload?.data !== undefined) merged.data = structuredClone(patch.payload.data);
  if (patch.payload?.layout !== undefined) merged.layout = structuredClone(patch.payload.layout);
  return merged;
}

export function loadDraft(projectId: string | undefined, plotId: string): LocalDraftRecord | null {
  try {
    const raw = localStorage.getItem(storageKey(projectId, plotId));
    if (!raw) return null;
    const rec: LocalDraftRecord = JSON.parse(raw);
    // TTL validation on head
    const ttlMs = DraftConfig.DRAFT_TTL_HOURS * 3600 * 1000;
    const headOk = rec.head && (nowMs() - (rec.head.updatedAt || 0) <= ttlMs);
    if (!headOk) {
      // Drop expired
      localStorage.removeItem(storageKey(projectId, plotId));
      return null;
    }
    return rec;
  } catch {
    return null;
  }
}

export interface SaveDraftOptions {
  projectId?: string;
  plotId: string;
  baselineHash: string;
  baselineFigure?: any; // optional but enables partial patch calculation
  workingFigure: any;
  // If provided, a previously validated head can be promoted to last_good before overwrite
  promoteLastGood?: boolean;
}

export function saveDraft(opts: SaveDraftOptions): { ok: boolean; bytes?: number; reason?: string; record?: LocalDraftRecord } {
  try {
    const { projectId, plotId, baselineHash, baselineFigure, workingFigure, promoteLastGood } = opts;
    const key = storageKey(projectId, plotId);
    const prev = loadDraft(projectId, plotId) || { plotId, projectId } as LocalDraftRecord;
    const patch = buildDraftPatch(baselineHash, baselineFigure, workingFigure);
    const rec: LocalDraftRecord = { ...prev };
    if (promoteLastGood && prev.head) {
      // Validate previous head is applicable to the baseline before promoting
      try {
        const testApplied = applyDraftPatch(baselineFigure, prev.head as DraftPatch);
        if (testApplied !== undefined) {
          rec.last_good = prev.head;
        }
      } catch {}
    }
    // TTL prune last_good
    try {
      const ttlMs = DraftConfig.DRAFT_TTL_HOURS * 3600 * 1000;
      if (rec.last_good && (nowMs() - (rec.last_good.updatedAt || 0) > ttlMs)) {
        delete rec.last_good;
      }
    } catch {}
    if (!rec.dirtySince) rec.dirtySince = nowMs();
    rec.head = patch;

    // Size guard (per-plot)
    const bytes = approxSizeBytes(rec);
    if (bytes > DraftConfig.MAX_DRAFT_BYTES) {
      // Signal caller to fallback to IndexedDB adapter
      return { ok: false, bytes, reason: 'draft_too_large' };
    }

    localStorage.setItem(key, JSON.stringify(rec));
    return { ok: true, bytes, record: rec };
  } catch (e: any) {
    return { ok: false, reason: 'write_failed' };
  }
}

export function clearDraft(projectId: string | undefined, plotId: string) {
  try { localStorage.removeItem(storageKey(projectId, plotId)); } catch {}
}

export function markActiveSession(projectId: string | undefined, plotId: string, sessionId?: string) {
  try {
    if (sessionId && sessionId.length > 0) {
      const key = `${activeKeyPrefix(projectId, plotId)}${sessionId}`;
      localStorage.setItem(key, '1');
    } else {
      // Back-compat fallback (pre-sessionized key)
      localStorage.setItem(legacyActiveKey(projectId, plotId), '1');
    }
  } catch {}
}

export function clearActiveSession(projectId: string | undefined, plotId: string, sessionId?: string) {
  try {
    if (sessionId && sessionId.length > 0) {
      const key = `${activeKeyPrefix(projectId, plotId)}${sessionId}`;
      localStorage.removeItem(key);
    }
  } catch {}
  // Back-compat: also clear legacy single-marker key
  try { localStorage.removeItem(legacyActiveKey(projectId, plotId)); } catch {}
}

export function clearActiveSessionMarkers(projectId: string | undefined, plotId: string) {
  try {
    const prefix = activeKeyPrefix(projectId, plotId);
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i) || '';
      if (k.startsWith(prefix)) toRemove.push(k);
    }
    for (const k of toRemove) localStorage.removeItem(k);
    // Also remove legacy key if present
    localStorage.removeItem(legacyActiveKey(projectId, plotId));
  } catch {}
}

export function wasUncleanClose(projectId: string | undefined, plotId: string): boolean {
  try {
    // Any session-scoped marker indicates previous session did not clear on unmount
    const prefix = activeKeyPrefix(projectId, plotId);
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i) || '';
      if (k.startsWith(prefix)) return true;
    }
    // Back-compat legacy single key
    if (localStorage.getItem(legacyActiveKey(projectId, plotId)) === '1') return true;
    return false;
  } catch { return false; }
}


